import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Award } from "lucide-react";
import type { AchievementDto } from "@/types/api";
import { standingsApi } from "@/api/standings";
import { useScope } from "@/app/useScope";
import { RequireContext, ErrorState, PageLoader, EmptyState } from "@/components/StateViews";
import { cn } from "@/lib/utils";

export function BadgesPage() {
  return (
    <RequireContext>
      <Badges />
    </RequireContext>
  );
}

function Badges() {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["achievements", scope.leagueId, scope.tournamentId],
    queryFn: () => standingsApi.achievements(scope),
  });

  const groups = useMemo(() => {
    const map = new Map<string, AchievementDto[]>();
    for (const a of q.data ?? []) {
      if (!map.has(a.group)) map.set(a.group, []);
      map.get(a.group)!.push(a);
    }
    return [...map.entries()];
  }, [q.data]);

  const earnedCount = (q.data ?? []).filter((a) => a.earned).length;

  if (q.isLoading) return <PageLoader />;
  if (q.error) return <ErrorState error={q.error} />;
  if ((q.data ?? []).length === 0)
    return <EmptyState title="Nincsenek kitűzők" icon={<Award className="size-7" />} />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Award className="size-5 text-primary" /> Kitűzők
        </h1>
        <p className="text-sm text-muted-foreground">
          Megszerezve: <span className="font-semibold text-foreground">{earnedCount}</span> / {q.data!.length}
        </p>
      </div>

      {groups.map(([group, items]) => (
        <section key={group}>
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">{group}</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((a) => (
              <BadgeCard key={a.code} a={a} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function BadgeCard({ a }: { a: AchievementDto }) {
  const showProgress = !a.earned && a.target != null && a.target > 0;
  const pct = showProgress ? Math.min(100, Math.round(((a.current ?? 0) / a.target!) * 100)) : 0;
  return (
    <div
      className={cn(
        "flex gap-3 rounded-xl border p-3",
        a.earned ? "border-primary/40 bg-primary/5" : "border-border bg-card/40",
      )}
    >
      <div className={cn("grid size-11 shrink-0 place-items-center rounded-lg text-2xl", a.earned ? "bg-primary/15" : "bg-muted grayscale opacity-60")}>
        <span aria-hidden>{a.icon}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className={cn("font-medium", !a.earned && "text-muted-foreground")}>{a.title}</div>
        <p className="text-xs text-muted-foreground">{a.description}</p>
        {showProgress && (
          <div className="mt-1.5">
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-0.5 text-[0.65rem] tabular-nums text-muted-foreground">
              {a.current ?? 0} / {a.target}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
